import Vue from "vue";
import AlarmContent from "@/components/toastification/AlarmContent.vue";
import store from "@/store";
import { EventBus } from "@/libs/event-bus";
import LayoutApi from "@/libs/LayoutApi";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import { encryptData } from "@/utils/encryptUserDataUtils";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import i18n from "@/libs/i18n";
import axios from "@axios";
import { format } from "date-fns";
import { translatePlanningNotificationStatus } from "@/utils/planningNotificationLabels";

const t = (key, values) => {
  if (!i18n || typeof i18n.t !== "function") return key;
  return values === undefined ? i18n.t(key) : i18n.t(key, values);
};

const formatStatusDate = (value) => {
  if (!value) return new Date().toISOString().slice(0, 19).replace("T", " ");
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? new Date().toISOString().slice(0, 19).replace("T", " ")
    : date.toISOString().slice(0, 19).replace("T", " ");
};

const normalizeStatusName = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "re_assigned") return "reassigned";
  return normalizedStatus;
};

const normalizeAssignedUnitTask = ({payload, taskData}) => ({
  ...payload,
  ...taskData,
  id_form_answer: taskData?.id_form_answer ?? payload?.id_form_answer ?? null,
  id_form: taskData?.id_form ?? payload?.id_form ?? null,
  id_alarm: taskData?.id_alarm ?? payload?.id_alarm ?? taskData?.alarm_id ?? payload?.alarm_id ?? null,
  alarm: taskData?.alarm ?? payload?.alarm ?? null,
});

let activeUserPrivateChannelName = null;

const actions = {
  showMessage({ state }, payload) {
    if (state.alarms.filter((alarm) => alarm.id != payload.data.id)) {
      Vue.$toast({
        component: AlarmContent,
        position: "top-right",
        timeout: 10000,
        props: {
          title: payload.title,
          variant: payload.variant,
          icon: "BellIcon",
          text: payload.msg,
          data: payload.data,
        },
      });
    }
  },

  setOperationalSecurityWS({}) {
    const user = this.getters["user/getUser"];

    window.Echo.private(`op-security-form-validations-${user.id}`).listen(
      ".status.updated",
      (payload) => {
        const { id, id_form_answer } = payload;

        EventBus.emit("action-plan", {
          id,
          type: "operational_security_form_validation",
          name: "FSO",
          action_plan: {
            id_form_answer,
          },
        });
      }
    );
  },

  setShowUserViewWS({ state, commit, dispatch }) {
    const user = this.getters["user/getUser"];

    window.Echo.private(`show-user-view-${user.id}`).listen(".update", (data) => {
      if (data.message.id === "default_alarm_view") {
        dispatch("setDefaultAlarmView", data.message.alarm_data);
      } else {
        const viewData = data.message.data;

        if (!Object.keys(state.tabs).includes(viewData.id)) {
          dispatch("addNewTab", { viewData });
        }
        dispatch("setActiveTab", viewData.id);
      }
    });
  },

  setActionPlanWS({ state, commit, dispatch }) {
    const user = this.getters["user/getUser"];

    window.Echo.private(`show-action-plan-${user.id}`).listen(
      ".update",
      async (wsData) => {
        const data = wsData.message;
        const layoutApi = new LayoutApi(this);
        const newTab = await layoutApi.addNewTab(data.action_plan_name, "default");
        layoutApi.setActiveTab(newTab.id);
        const currentTabId = layoutApi.getCurrentTabId();

        setTimeout(() => {
          if (newTab.id === currentTabId) {
            const slotInfo = {
              name: `action_plan_${newTab.id}`,
              position: 3,
              data: {
                id_action_plan: data.id_action_plan,
                id_alarm: data.id_alarm,
                in_app_source: SOURCES.COMMAND,
              },
              slotType: SLOT_TYPES.ACTION_PLAN,
            };
            layoutApi.setSlot(
              slotInfo.name,
              slotInfo.position,
              slotInfo.data,
              slotInfo.slotType
            );
          }
        }, 500);
      }
    );
  },

  setAlarmsWS({ state, commit, dispatch }, { force = false } = {}) {
    const user = store.state.user.user;
    const channelName = `user-${user.id}`;

    window.Echo.private(`user-${user.id}`)
      .listen(".Alarm", async (data) => {
        try {
          if (!data?.id) {
            console.warn("Evento Alarm sin id:", data);
          } else {
            let token = localStorage.getItem("accessToken");
            if (
              data.host !== process.env.VUE_APP_BASE_URL &&
              store.state.user.user.external_users.length > 0
            ) {
              token = store.state.user.user.external_users.find(
                (external_user) => external_user.host == data.host
              ).token;
            }

            const { data: resp } = await axios.get(`${data.host}/v1/alarm/${data.id}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "X-Host": process.env.VUE_APP_BASE_URL,
                "X-localization": this.locale,
              },
            });

            const serverData = resp?.data;
            data = { ...data, ...serverData };
          }
        } catch (err) {
          console.error("Error obteniendo alarma por ID:", err);
          return;
        }

        EventBus.emit("update-alarms", data);
        dispatch("addNotificationAlarm", data);

        // if (!isForCurrentUser) {
        //   return;
        // }

        // const isAssignedToCurrentUser = hasAssignedUserFilter && (Number(assignedUserId) === Number(currentUserId));
        // const alarmPayload = isAssignedToCurrentUser
        //   ? {...data, is_assigned_to_current_user: true}
        //   : data;
        const alarmPayload = data;

        if (
          alarmPayload.is_assigned_to_current_user === true &&
          alarmPayload.alarm_status &&
          alarmPayload.alarm_status !== "waiting"
        ) {
          dispatch("removeNotificationAlarm", { id: alarmPayload.id });
        } else {
          dispatch("addNotificationAlarm", alarmPayload);
        }

        const isReminder = data.is_reminder === true;
        const shouldShowToast =
          this.getters["user/getPreferences"].show_alarm_notifications &&
          (data.event === "created" ? data.alarm_status === "waiting" : isReminder);

        if (data.event === "created" || isReminder) {
          if (shouldShowToast) {
            dispatch("showMessage", {
              title: null,
              msg: null,
              variant: "success",
              data: alarmPayload,
            });
          }

          if (data.event === "created" && data.alarm_level?.path) {
            const audio = new Audio(data.alarm_level.path);
            audio.play();
          }
        }

        if (data.event === "created") {
          if (data.detection_event_data && data.detection_event_data.id_camera_proxy) {
            const cameraProxyId = data.detection_event_data.id_camera_proxy;
            EventBus.emit(`new_alarm:${cameraProxyId}`, data);
          }
        }

        if (store.state.sip.presavedAlarm.alarmJson) {
          console.log(store.state.sip.presavedAlarm.alarmJson);
          if (store.state.sip.presavedAlarm.alarmJson.id == data.id) {
            if (data.alarm_status) {
              store.dispatch("sip/changePresavedAlarmStatus", data.alarm_status);
              EventBus.emit("change-alarm-status", data.alarm_status);
            }
          }
        }
      })
      .listen(".user-region-alarms-synced", async (data) => {
          console.log("Alarmas sincronizadas:", data);
          EventBus.emit("update-alarms");
     })
      // Task assigned to user units
      .listen(".device-assignment-task.updated", async (payload) => {
        const { id, status, id_unit, comment, notified_user_id, subject } = payload;
        console.log("payload", payload);

        try {
          const { data } = await axios.get(`/v1/device_assignment_task_user_units/${id}`);
          const taskData = data || payload;
          const normalizedTaskData = normalizeAssignedUnitTask({payload, taskData});
          const isNotificationFormTask = normalizedTaskData.type === 'notification-form';
          const isAlarmTask = normalizedTaskData.type === 'alarm';
          const isInAppNotificationTask = normalizedTaskData.type === 'notification';
          const isPanelFailureTask = normalizedTaskData.type === 'panel-failure';
          const notificationSubject = String(normalizedTaskData.subject ?? subject ?? '').trim();
          const hasNotificationSubject = notificationSubject !== '';
          const isFormTask =
            normalizedTaskData.type === "form" ||
            normalizedTaskData.type === "answered_form" ||
            normalizedTaskData.type === "form-created-with-relation" ||
            isNotificationFormTask;
          const isSecurityFormTask = normalizedTaskData.type === "answered_form" || isNotificationFormTask;
          const titleKey = isSecurityFormTask
            ? 'unit.assigned_security_form_task'
            : (isFormTask ? 'unit.assigned_form_task' : 'unit.assigned_task');
          const titleLabel = t(titleKey);
          const formLabelFallback = t('unit.form_label_fallback');
          const alarmIdentifierLabel = normalizedTaskData.id_alarm != null
            ? `Alarma Id ${normalizedTaskData.id_alarm}`
            : 'Alarma Id';
          const panelFailureLabel = t('unit.panel_failure_task');
          const titleValue = isFormTask
            ? (normalizedTaskData.form?.name || formLabelFallback)
            : ((isAlarmTask || isPanelFailureTask) ? alarmIdentifierLabel : (normalizedTaskData.unit?.description || t('unit.single')));
          const actionPlanName = isPanelFailureTask
            ? `${panelFailureLabel}: ${titleValue}`
            : (isAlarmTask
              ? (hasNotificationSubject ? notificationSubject : `Notificación: ${titleValue}`)
              : (isInAppNotificationTask
                ? (hasNotificationSubject
                  ? notificationSubject
                  : `Notificación: ${comment || normalizedTaskData.comment || ''}`)
              : (isNotificationFormTask
              ? `Notificación: ${titleValue}`
              : `${titleLabel}: ${titleValue}`)));
          const subtitle = isFormTask
            ? ""
            : normalizedTaskData.unit?.router?.tenant?.name ||
            normalizedTaskData.unit?.region?.name ||
            "";
          const statusDate = format(new Date(), "yyyy-MM-dd HH:mm:ss");
          const toastTitle = isPanelFailureTask
            ? panelFailureLabel
            : ((isAlarmTask || isInAppNotificationTask)
              ? (hasNotificationSubject ? notificationSubject : 'Notificación')
              : titleLabel);
          const toastText = isPanelFailureTask
            ? `${titleValue}: ${comment || normalizedTaskData.comment || t('unit.panel_failure_task_hint')}`
            : (isAlarmTask
            ? (hasNotificationSubject ? (comment || normalizedTaskData.comment || 'Notificación') : actionPlanName)
            : (isInAppNotificationTask
              ? (comment || normalizedTaskData.comment || 'Notificación')
            : (isNotificationFormTask
              ? actionPlanName
              : `${titleValue}: ${comment || normalizedTaskData.comment || ''}`)));

          Vue.$toast({
            component: ToastificationContent,
            position: "top-right",
            timeout: 10000,
            props: {
              title: toastTitle,
              text: toastText,
              variant: "info",
              icon: "BellIcon",
            },
          });

          EventBus.emit("action-plan", {
            id: `unit-task-${id}`,
            taskId: id,
            type: "unit_task",
            name: "UT",
            action_plan: {
              name: actionPlanName,
            },
            user: {
              user: subtitle,
            },
            status: status,
            statusDate,
            taskData: normalizedTaskData,
          });
        } catch (error) {
          Vue.$toast({
            component: ToastificationContent,
            position: "top-right",
            timeout: 10000,
            props: {
              title: t("unit.assigned_task"),
              text: comment || t("unit.assigned_task"),
              variant: "info",
              icon: "BellIcon",
            },
          });
        }
      })
      // Operational procedure status updates on the user-specific channel
      .listen(".operational-procedure.status.updated", (payload) => {
        const { id, status, reference_number, unit_name } = payload;
        const statusDate = formatStatusDate(payload.updated_at || payload.created_at);

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: `${t("operational_procedure_exercise.title")}`,
            text: `${reference_number} | ${t("risk.status_notification")}: ${t(
              "risk.status_" + status
            )}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "operational_procedure",
          name: "OP",
          action_plan: {
            name: `${t("operational_procedure_exercise.title")} ${reference_number} : ${t(
              "risk.status_" + status
            )}`,
          },
          user: { user: unit_name },
          route: `/risk/operational_procedure?id=${id}`,
          statusDate,
        });
      })
      // Risk evaluation status updates
      .listen(".risk-evaluation.status.updated", (payload) => {
        const { id, status, reference_number, unit_name } = payload;

        try {
          store.dispatch("risk_evaluation/updateLocalStatus", { id, status });
        } catch (_) {}

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: `${t("risk.title_notification")}`,
            text: `${reference_number} | ${t("risk.status_notification")}: ${t(
              "risk.status_" + status
            )}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "risk",
          name: "RE",
          action_plan: {
            name: `${t("risk.title_notification")} ${reference_number} : ${t(
              "risk.status_" + status
            )}`,
          },
          user: { user: unit_name },
          route: `/risk/evaluations?id=${id}`,
          statusDate: formatStatusDate(payload.updated_at || payload.created_at),
        });
      })
      // Simulacrum status updates
      .listen(".simulacrum.status.updated", (payload) => {
        const { id, status, reference_number, unit_name } = payload;

        try {
          store.dispatch("simulacrum/updateLocalStatus", { id, status });
        } catch (_) {}

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: `${t("simulacrum.simulacrums_title")}`,
            text: `${reference_number} | ${t("risk.status_notification")}: ${t(
              "risk.status_" + status
            )}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "simulacrum",
          name: "SI",
          action_plan: {
            name: `${t("simulacrum.simulacrums_title")} ${reference_number} : ${t(
              "risk.status_" + status
            )}`,
          },
          user: { user: unit_name },
          route: `/risk/simulacrums?id=${id}`,
          statusDate: formatStatusDate(payload.updated_at || payload.created_at),
        });
      })
      .listen(".bau_complement.status.updated", (payload) => {
        const { id, reference_number, unit_name, users_involved } = payload;
        const status = normalizeStatusName(payload.status);
        const currentUserId = Number(user.id);
        const isUsersInvolvedArray = Array.isArray(users_involved);
        const shouldProcessForUser =
          !isUsersInvolvedArray ||
          users_involved.some(
            (involvedUserId) => Number(involvedUserId) === currentUserId
          );

        if (!shouldProcessForUser) {
          return;
        }

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: `${t("bau_complement.title")}`,
            text: `${reference_number} | ${t("risk.status_notification")}: ${t(
              "risk.status_" + status
            )}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "bau_complement",
          name: "BAU",
          action_plan: {
            name: `${t("bau_complement.title")} ${reference_number} : ${t(
              "risk.status_" + status
            )}`,
          },
          user: { user: unit_name },
          route: `/risk/bau-complements?id=${id}`,
          status,
          statusDate: formatStatusDate(payload.updated_at || payload.created_at),
        });
      })
      // Policy exception status updates
      .listen(".policy_exception.status.updated", (payload) => {
        const { id, status, reference_number, unit_name } = payload;

        try {
          store.dispatch("policyException/updateLocalStatus", { id, status });
        } catch (_) {}

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: `${t("policy_exception.title")}`,
            text: `${reference_number} | ${t("risk.status_notification")}: ${t(
              "risk.status_" + status
            )}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "policy_exception",
          name: "EP",
          action_plan: {
            name: `${t("policy_exception.title")} ${reference_number} : ${t(
              "risk.status_" + status
            )}`,
          },
          user: { user: unit_name },
          route: `/policy-exceptions/${id}`,
          statusDate: formatStatusDate(new Date()),
        });
      })
      // Planning status updates
      .listen(".planning.status.updated", (payload) => {
        const { id, status, unit_name } = payload;
        const statusDate = format(new Date(), "yyyy-MM-dd HH:mm:ss");
        const title = t("planning.toast_planning_title");
        const idLine = t("planning.toast_planning_id_line", { id });
        const statusLabel = translatePlanningNotificationStatus(t, status);

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title,
            text: `${idLine} | ${t("risk.status_notification")}: ${statusLabel}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "planning",
          name: "PL",
          action_plan: { name: `${title} — ${idLine} — ${statusLabel}` },
          user: { user: unit_name },
          route: `/planning/${id}`,
          status,
          statusDate,
        });
      })
      // Security self audit status updates
      .listen(".security-self-audit.status.updated", (payload) => {
        const { id, status, reference_number, unit_name } = payload;

        try {
          store.dispatch("security_self_audits/updateLocalStatus", { id, status });
        } catch (_) {}

        Vue.$toast({
          component: ToastificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: `${t("security_self_audit.title")}`,
            text: `${reference_number} | ${t("risk.status_notification")}: ${t(
              "risk.status_" + status
            )}`,
            variant: "success",
            icon: "BellIcon",
          },
        });

        EventBus.emit("action-plan", {
          id,
          type: "security_self_audit",
          name: "SSA",
          action_plan: {
            name: `${t("security_self_audit.title")} ${reference_number} : ${t(
              "risk.status_" + status
            )}`,
          },
          user: { user: unit_name },
          route: `/risk/security_self_audit/audits?id=${id}`,
          statusDate: formatStatusDate(payload.updated_at || payload.created_at),
        });
      });

    // avoid duplicate listeners
    activeUserPrivateChannelName = channelName;
  },

  setUserSetUp({ state, commit, dispatch }) {
    const user = store.state.user.user;

    window.Echo.private(`user-setup-${user.id}`)
      .listen(".call", (data) => {
        EventBus.emit(`call_device`, { id: data.device_id });
      })
      .listen(".updateConfig", async (data) => {
        const userData = data;
        userData.ability = [];
        if (userData.video_wall) {
          store
            .dispatch("video_wall/online", userData)
            .then((response) => {
              console.log(response);
            })
            .catch((response) => console.log(response));
        }
        if (userData.is_master_admin) {
          userData.ability.push({
            action: "manage",
            subject: "all",
          });
        } else {
          userData.ability = data.abilities;
          userData.ability.push({
            action: "read",
            subject: "Auth",
          });

          userData.ability.push({
            action: "read",
            subject: "Home",
          });
        }
        // Update the encrypted userData in localStorage
        const userDataEncrypted = encryptData(userData);
        localStorage.setItem("userData", userDataEncrypted); //update userData local storage

        store.dispatch("user/setUserDataFromLocalStorage", userData); //update user state on store
        dispatch("updateAlarms"); //update alarms
        EventBus.emit("refresh_navbar_elements_for_user_update", userData); //update navbar horizontal
        //Compare units if are the same dont update, valid if it's better to have 2 events, one for the units and other for the config.
        await store.dispatch("grid/resetUnits"); //reset units
        // units = {...store.state.grid.units }
        // units.filter ()
      });

    window.Echo.private(`user-${user.id}`)
      .listen(".ExternalUserCreated", (data) => {
        let userData = JSON.parse(localStorage.getItem("userData"));
        userData.external_users.push(data);
        // Encrypt before storing
        const userDataEncrypted = encryptData(userData);
        localStorage.setItem("userData", userDataEncrypted);
        store.dispatch("user/setUserDataFromLocalStorage", userData);
        dispatch("updateAlarms");
      })
      .listen(".ExternalUserDeleted", (data) => {
        let userData = JSON.parse(localStorage.getItem("userData"));
        userData.external_users = userData.external_users.filter(
          (ext) => ext.id != data.id
        );
        // Encrypt before storing
        const userDataEncrypted = encryptData(userData);
        localStorage.setItem("userData", userDataEncrypted);
        store.dispatch("user/setUserDataFromLocalStorage", userData);
        dispatch("updateAlarms");
      });

    if (user.enabled_helpdesk) {
      window.Echo.private(`ticket-event-${user.id}`).listen(".create", (data) => {
        store.dispatch("helpdesk_ticket/getAssigned", {
          assigned: user.id,
          id_user: user.id,
        });
      });
    }
  },

  setUpdateDeviceStatusWS({ state, commit, dispatch }) {
    window.Echo.private(`update-device-status`).listen(".update", (data) => {
      const payload = {
        data: {
          tenantId: data.tenant_id,
          facility: {
            id: data.facility_id, // required
            // icon: "custom custom-circle-offline"
          },
          device: {
            id: data.device_proxy_id, // required
            icon: `custom custom-camera-${data.online ? "online" : "offline"}`,
          },
        },
      };

      store.dispatch("grid/setNodeData", payload);
    });
  },

  setSubsystemTreeMaintenanceWS() {
    if (!window.Echo) return;

    // The event is expected to carry the unit's real flags. Today every producer fills
    // only the one it owns and hardcodes the other, so a state can still be cleared by
    // mistake; that is a backend fix, not a client one.
    window.Echo.private("facility-under-maintenance").listen(".update", (data) => {
      const facilityId = data?.facility_id ?? null;
      const tenantId = data?.tenant_id ?? null;
      const inProject = data?.in_project;
      const underMaintenance = data?.under_maintenance;

      if (facilityId == null || (inProject === undefined && underMaintenance === undefined)) return;

      store.commit("grid/setFacilityMaintenanceStatus", { facilityId, tenantId, inProject, underMaintenance });
    });
  },

  setTakenAlarmWs() {
    window.Echo.private("taken-alarms").listen(".update-list", (data) => {
      store.dispatch("taken_alarms/getTakenAlarms");
    });
  },

  setShowAlarmViewWS() {
    const user = this.getters["user/getUser"];

    window.Echo.private(`show-alarm-view-${user.id}`).listen(".update", async (data) => {
      console.log("setShowAlarmViewWS:data", data);
      if (data.alarm) {
        await store.dispatch("grid/setAlarmView", { alarm: data.alarm });
      }
    });
  },
};

export default {
  actions,
};
